import { memoize } from 'micro-memoize';
import { Query, QueryKey } from '@tanstack/query-core';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { createUpdateCurrentUserMutation } from '../../../mutations/currentUser';
import {
  currentUserQuery,
  currentUserQueryKey,
  CurrentUserResult,
  refreshCurrentUserData
} from '../../../queries/currentUser';
import { queryClient } from '../../../core/queryClient';
import { userPropertiesQuery, UserPropertiesResult } from '../../../queries/userProperties';

export interface CurrentUserViewOptions {
  enabled?: boolean;
  includeProperties?: boolean;
}

export interface CurrentUserViewState extends ApiViewState {
  currentUser?: CurrentUserResult;
  errorLoadingProperties: Error | null;
  errorUpdating: Error | null;
  isLoadingProperties: boolean;
  isPending: boolean;
  isRefreshing: boolean;
  isUpdating: boolean;
  properties?: UserPropertiesResult;
}

/**
 * Composes two independently cached resources, configured in queries/currentUser.ts and queries/userProperties.ts:
 * - ['current-user', 'profile']: backend profile, fresh for 30 minutes.
 * - ['current-user', 'properties']: property array, fresh for 5 minutes; observed only with includeProperties.
 * Separate entries let properties refresh or fail without refetching or blocking the profile.
 * The combined currentUser object exists only in view state, not as a third cache entry.
 * Freshness does not schedule polling or delete cached data when the interval elapses.
 * The clear*QueryCache helpers invalidate entries (mark stale); session resets remove them.
 * Cached profile data is not proof of authentication; the authentication SDK remains authoritative.
 */
export class CurrentUserView extends ApiView {
  // Keep the combined object's identity stable when neither cached resource has changed.
  private combineUser = memoize((profile: CurrentUserResult, properties: UserPropertiesResult) => ({
    ...profile,
    properties
  }));
  private currentUserObs: ReturnType<typeof currentUserQuery>;
  private propertiesObs?: ReturnType<typeof userPropertiesQuery>;
  private updateMutation: ReturnType<typeof createUpdateCurrentUserMutation>;

  public state: CurrentUserViewState = {
    ...getDefaultApiViewState(),
    currentUser: undefined,
    errorLoadingProperties: null,
    errorUpdating: null,
    isLoadingProperties: false,
    isPending: true,
    isRefreshing: false,
    isUpdating: false
  };

  constructor(private options: CurrentUserViewOptions = {}) {
    super();
    // Register observers now; ApiView starts their subscriptions when a consumer subscribes to the view.
    this.currentUserObs = this.addQuery(currentUserQuery({ enabled: options.enabled !== false }));
    if (options.includeProperties) {
      this.propertiesObs = this.addQuery(userPropertiesQuery({ enabled: options.enabled !== false }));
    }
    this.updateMutation = this.addMutation(createUpdateCurrentUserMutation());
    // Expose any existing cached data on the first render without starting fetches in the constructor.
    this.updateState();
  }

  // * Public API *

  /** Explicitly refreshes the profile even if it is still fresh; properties are unaffected. */
  public refresh() {
    return refreshCurrentUserData();
  }

  public subscribe(onChange: () => void) {
    super.subscribe(onChange);
    // Query removal/replacement needs extra handling: observers can retain snapshots of removed entries.
    // Register this listener's cleanup with ApiView so destroy() also unsubscribes it.
    this.addUnsubscribe(
      queryClient.getQueryCache().subscribe(event => {
        if (event.type === 'updated' && event.action.type === 'success') {
          this.reattachQueries(event.query);
        }
        if (
          event.type === 'removed' &&
          (event.query === this.currentUserObs.getCurrentQuery() ||
            event.query === this.propertiesObs?.getCurrentQuery())
        ) {
          this.notify();
        }
      })
    );
  }

  /** Runs the guarded save through a mutation observer, exposing its pending/error state to consumers. */
  public updateCurrentUser(data: Parameters<typeof this.updateMutation.mutate>[0]['data']) {
    return this.updateMutation.mutate(this.mutationContext(data, currentUserQueryKey()));
  }

  // * Private / Protected API *

  private get currentUser() {
    const query = this.currentUserObs.getCurrentQuery();
    const cachedQuery = queryClient.getQueryCache().get<CurrentUserResult>(query.queryHash);
    // Hide a retained snapshot after logout or query replacement, even if the same user signs in again.
    const profile =
      this.options.enabled !== false && cachedQuery === query ? this.getResult(this.currentUserObs).data : undefined;
    return profile && this.properties ? this.combineUser(profile, this.properties) : profile;
  }

  /** Projects observer results into UI state; ApiView publishes a new snapshot when that state changes. */
  protected getNewState(): CurrentUserViewState {
    const result = this.getResult(this.currentUserObs);
    const mutation = this.updateMutation.getCurrentResult();

    return {
      ...super.getNewState(),
      currentUser: this.currentUser,
      errorLoading: result.error,
      errorLoadingProperties: this.propertiesObs?.getCurrentResult().error ?? null,
      errorUpdating: mutation.error,
      isLoading: result.isLoading,
      isLoadingProperties: this.propertiesObs?.getCurrentResult().isPending ?? false,
      isPending: result.isPending,
      // Includes background refetches, when an existing profile can still be displayed.
      isRefreshing: result.isFetching,
      isUpdating: mutation.isPending,
      properties: this.properties
    };
  }

  /**
   * Capture query identity at the save call, before the mutation observer starts its async work.
   * Removal/replacement invalidates this guard; a normal refetch of the same query does not.
   */
  private mutationContext<TData>(data: TData, queryKey: QueryKey) {
    const query = queryClient.getQueryCache().build<unknown>(queryClient, { queryKey });
    return {
      data,
      isCurrent: () => queryClient.getQueryCache().find({ exact: true, queryKey }) === query
    };
  }

  private get properties() {
    const query = this.propertiesObs?.getCurrentQuery();
    // Apply the same removed/replaced-query protection as the profile getter.
    return this.options.enabled !== false && query && queryClient.getQueryCache().get(query.queryHash) === query
      ? this.propertiesObs?.getCurrentResult().data
      : undefined;
  }

  /**
   * A mounted view can outlive a session reset. Reapplying options makes its observers look up
   * the replacement queries by key after new data is seeded or fetched successfully.
   */
  private reattachQueries(query: Query) {
    const profileQuery = this.currentUserObs.getCurrentQuery();
    if (query.queryHash === profileQuery.queryHash && query !== profileQuery) {
      this.currentUserObs.setOptions(this.currentUserObs.options);
      this.propertiesObs?.setOptions(this.propertiesObs.options);
    }
    const propertiesQuery = this.propertiesObs?.getCurrentQuery();
    if (propertiesQuery && query.queryHash === propertiesQuery.queryHash && query !== propertiesQuery) {
      this.propertiesObs?.setOptions(this.propertiesObs.options);
    }
  }
}
